import { API_END_POINTS } from '@/shared/api/end-points'
import { ApiResponse } from '@/shared/api/types'
import { API_URL } from '@/shared/api/constants'
import { getCachedSession, clearSessionCache } from '@/shared/api/client-api'

export enum UploadStatus {
  PENDING = 'pending',
  SUCCESS = 'success',
  ERROR = 'error',
}

export interface UploadProjectFileChunkRequest {
  projectId: string
  file: Blob
  fileName: string
  chunkIndex: number
  totalChunks: number
  totalSize: number
}

export interface UploadProjectFileChunkPendingData {
  fileName: string
  userId: number
  chunkIndex: number
  totalChunks: number
  uploadedChunks: number
  actualSize: number
  totalSize: number
  message: string
}

export interface UploadProjectFileChunkSuccessData {
  uuid: string
  fileName: string
  path: string
  url: string
  userId: number
  chunkIndex: number
  totalChunks: number
  uploadedChunks: number
  actualSize: number
  totalSize: number
  message: string
  projectId: string
}

export interface UploadProjectFileChunkResponse {
  status: UploadStatus
  data: UploadProjectFileChunkPendingData | UploadProjectFileChunkSuccessData
}

const uploadProjectFileService = async (
  data: UploadProjectFileChunkRequest,
): Promise<ApiResponse<UploadProjectFileChunkResponse>> => {
  const session = await getCachedSession()
  const token = session?.user?.token
  const userId = session?.user?.data?.id || 0

  const formData = new FormData()
  formData.append('file', data.file)
  formData.append('fileName', data.fileName)
  formData.append('chunkIndex', data.chunkIndex.toString())
  formData.append('totalChunks', data.totalChunks.toString())
  formData.append('totalSize', data.totalSize.toString())

  let url = `${API_URL}/${API_END_POINTS.project.uploadProjectFile({ projectId: data.projectId })}`
  url = url.replace(/users\/[^/]+/, `users/${userId}`)

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    })

    const result = await response.json()

    if (response.ok) {
      return {
        ok: true,
        status: response.status,
        result: result,
        response: response,
      } as ApiResponse<UploadProjectFileChunkResponse>
    } else {
      if (response.status === 401) {
        clearSessionCache()
      }

      return {
        ok: false,
        status: response.status,
        message: result.message || 'Upload failed',
        error: new Error(result.message || 'Upload failed'),
      } as ApiResponse<UploadProjectFileChunkResponse>
    }
  } catch (error) {
    console.error('Upload error:', error)
    return {
      ok: false,
      status: 500,
      message: 'Network error',
      error: error as Error,
    } as ApiResponse<UploadProjectFileChunkResponse>
  }
}

export default uploadProjectFileService
